import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { cn } from "../ui/utils";

interface RetireFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  formName: string;
}

export const RetireFormModal: React.FC<RetireFormModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  formName
}) => {
  const [confirmText, setConfirmText] = useState("");
  const [isRetiring, setIsRetiring] = useState(false);

  const handleConfirm = async () => {
    if (confirmText !== "DELETE") return;
    
    setIsRetiring(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setIsRetiring(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent hideClose={true} className="p-0 sm:max-w-lg border-none overflow-hidden bg-white shadow-2xl rounded-2xl">
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-red-600 flex items-center gap-2">
                <AlertTriangle className="w-6 h-6" />
                Retire Form
            </h3>
            <button 
                onClick={onClose}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-900"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-6">
            <div className="space-y-2">
                <p className="font-bold text-gray-900 text-lg">You are about to retire <span className="underline decoration-red-200">{formName}</span>.</p>
                <p className="text-red-600 font-bold uppercase tracking-tight text-sm">This action cannot be undone.</p>
            </div>

            <div className="space-y-3 bg-gray-50 p-4 rounded-xl border border-gray-100">
              <p className="font-bold text-gray-700 text-sm uppercase tracking-wider">What this means:</p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-gray-600 font-medium">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 shrink-0" />
                    <span>Users can no longer submit requests using this form</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-600 font-medium">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-400 mt-1.5 shrink-0" />
                    <span>Existing support tickets will not be affected</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-600 font-medium">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 shrink-0" />
                    <span>QR Codes and links will be terminated</span>
                </li>
              </ul>
            </div>

            <div className="space-y-3 pt-2">
              <label className="block text-sm font-bold text-gray-700">
                  Type <span className="font-black text-red-600 bg-red-50 px-1 rounded">DELETE</span> to continue
              </label>
              <Input 
                type="text" 
                className="w-full h-12 px-4 bg-gray-50 border-gray-200 rounded-xl focus:ring-red-500/20 focus:border-red-500 font-bold placeholder:text-gray-300 transition-all" 
                placeholder="Type DELETE" 
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
              />
            </div>

            <div className="flex items-center gap-3 pt-4">
              <Button 
                variant="outline"
                className="flex-1 h-12 rounded-xl border-gray-200 font-bold text-gray-600 hover:bg-gray-50 transition-all uppercase tracking-tight text-xs"
                onClick={onClose}
              >
                Cancel, Go Back
              </Button>
              <Button 
                disabled={confirmText !== "DELETE" || isRetiring}
                className="flex-1 h-12 bg-red-600 hover:bg-red-700 text-white rounded-xl shadow-lg shadow-red-600/20 transition-all font-bold uppercase tracking-tight text-xs"
                onClick={handleConfirm}
              >
                {isRetiring ? "Retiring..." : "Retire Form"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
